import React, { useState, useEffect } from 'react';
import { devLog } from '../utils/devTools';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Grid,
  Chip,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Stack,
  Divider,
  useMediaQuery,
  Tooltip,
  FormControlLabel,
  Switch,
  Snackbar
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import ComputerIcon from '@mui/icons-material/Computer';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  addWeeks, 
  subWeeks,
  isToday
} from 'date-fns';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import { ReporterAvailability, JobType, ReporterPreferences } from '../types/availability';
import { availabilityApi } from '../utils/availabilityApi';
import { preferencesApi } from '../utils/preferencesApi';
import { HourSlotsEncoder } from '../utils/hourSlotsEncoder';

// Hour slots for visual selection (7am-7pm = 12 hours)
const HOUR_SLOTS = Array.from({ length: 12 }, (_, i) => {
  const hour = 7 + i; // Start at 7am
  return {
    value: hour,
    label: hour <= 12 ? `${hour}${hour === 12 ? 'pm' : 'am'}` : `${hour - 12}pm`,
    time24: `${hour.toString().padStart(2, '0')}:00`
  };
});

// Quick selection presets (8am-5pm core business hours)
const QUICK_PRESETS = [
  { label: 'Morning', hours: [8, 9, 10, 11] }, // 8am-12pm
  { label: 'Afternoon', hours: [12, 13, 14, 15, 16] }, // 12pm-5pm  
  { label: 'All Day', hours: [8, 9, 10, 11, 12, 13, 14, 15, 16] } // 8am-5pm
];

// Helper function to determine default job types based on preferences
const getDefaultJobTypes = (preferences: ReporterPreferences | null): JobType[] => {
  if (!preferences || !preferences.statePreferences.length) {
    // No preferences set, default to virtual
    return ['virtual'];
  }
  
  const hasVirtualState = preferences.statePreferences.some(pref => pref.virtualAvailable);
  const hasInPersonState = preferences.statePreferences.some(pref => pref.inPersonAvailable);
  
  const defaults: JobType[] = [];
  if (hasVirtualState) defaults.push('virtual');
  if (hasInPersonState) defaults.push('in-person');
  
  // If somehow no states have either preference, default to virtual
  return defaults.length > 0 ? defaults : ['virtual'];
};

interface AvailabilityUpdate {
  hourSlots: string;
  notes: string;
  unavailable: boolean;
}

interface DayScheduleProps {
  date: Date;
  availability: ReporterAvailability | null;
  preferences: ReporterPreferences | null;
  onUpdate: (dateStr: string, updatedData: AvailabilityUpdate | null) => void;
  onNotification: (message: string, severity: 'success' | 'error') => void;
  isMobile?: boolean;
  canEdit?: boolean;
  editDeadline?: string;
}

const DaySchedule: React.FC<DayScheduleProps> = ({ date, availability, preferences, onUpdate, onNotification, isMobile = false, canEdit = true, editDeadline }) => {
  const theme = useTheme();
  const isLargeMobile = useMediaQuery('(max-width: 600px) and (min-height: 700px)');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeadlineWarning, setShowDeadlineWarning] = useState(false);
  const [selectedHours, setSelectedHours] = useState<Record<number, JobType[]>>({});
  const [newSlotJobTypes, setNewSlotJobTypes] = useState<JobType[]>(getDefaultJobTypes(preferences));
  const [notes, setNotes] = useState('');
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartHour, setDragStartHour] = useState<number | null>(null);
  const [dragStartWasSelected, setDragStartWasSelected] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isMarkedUnavailable, setIsMarkedUnavailable] = useState(false);

  // Get colors based on job types
  const getSlotColors = (jobTypes: JobType[] | undefined) => {
    if (!jobTypes || jobTypes.length === 0) {
      return {
        backgroundColor: 'transparent',
        borderColor: '#e0e0e0',
        hoverBackgroundColor: 'rgba(0,0,0,0.04)',
        hoverBorderColor: '#bdbdbd'
      };
    }

    // Color scheme based on job types
    const hasVirtual = jobTypes.includes('virtual');
    const hasInPerson = jobTypes.includes('in-person');

    if (hasVirtual && hasInPerson) {
      // Both - Purple (softer purple)
      return {
        backgroundColor: '#7e57c2',
        borderColor: '#7e57c2',
        hoverBackgroundColor: '#5e35b1',
        hoverBorderColor: '#5e35b1'
      };
    } else if (hasInPerson) {
      // In-person only - Soft Red (not aggressive)
      return {
        backgroundColor: '#e57373',
        borderColor: '#e57373',
        hoverBackgroundColor: '#d32f2f',
        hoverBorderColor: '#d32f2f'
      };
    } else {
      // Virtual only - Blue
      return {
        backgroundColor: '#42a5f5',
        borderColor: '#42a5f5',
        hoverBackgroundColor: '#1976d2',
        hoverBorderColor: '#1976d2'
      };
    }
  };

  const dateStr = format(date, 'yyyy-MM-dd');
  const dayIsToday = isToday(date);
  
  // Parse current availability into hour slots
  const getCurrentHourSlots = (): Record<number, JobType[]> => {
    if (!availability) return {};

    // Check for availability records (handle both date formats)
    const availabilityRecord = availability.availability.find(record => {
      // Handle both ISO timestamp format and plain date strings
      const recordDate = record.availabilityDate.includes('T') ? record.availabilityDate.split('T')[0] : record.availabilityDate;
      return recordDate === dateStr;
    });

    if (!availabilityRecord) return {};

    // Decode the hour_slots string into our hour slots object
    return HourSlotsEncoder.decode(availabilityRecord.hourSlots);
  };

  const getJobTypes = (): JobType[] => {
    if (!availability) return [];

    // Check for availability records (handle both date formats)
    const availabilityRecord = availability.availability.find(record => {
      const recordDate = record.availabilityDate.includes('T') ? record.availabilityDate.split('T')[0] : record.availabilityDate;
      return recordDate === dateStr;
    });

    if (!availabilityRecord) return [];

    // Get job types from the hour_slots encoding
    return HourSlotsEncoder.getJobTypes(availabilityRecord.hourSlots);
  };

  const getNotes = (): string => {
    if (!availability) return '';
    const availabilityRecord = availability.availability.find(record => {
      const recordDate = record.availabilityDate.includes('T') ? record.availabilityDate.split('T')[0] : record.availabilityDate;
      return recordDate === dateStr;
    });

    return availabilityRecord?.notes || '';
  };

  const getIsUnavailable = (): boolean => {
    if (!availability) return false;
    const availabilityRecord = availability.availability.find(record => {
      const recordDate = record.availabilityDate.includes('T') ? record.availabilityDate.split('T')[0] : record.availabilityDate;
      return recordDate === dateStr;
    });
    
    return availabilityRecord?.unavailable || false;
  };

  const currentHours = getCurrentHourSlots();
  const currentJobTypes = getJobTypes();
  const currentNotes = getNotes();
  const currentIsUnavailable = getIsUnavailable();
  const isAvailable = Object.keys(currentHours).length > 0 && !currentIsUnavailable;
  
  // Debug logging
  const selectedHourNumbers = Object.keys(selectedHours).map(h => parseInt(h));
  const currentHourNumbers = Object.keys(currentHours).map(h => parseInt(h));
  devLog(`🔍 Day ${format(date, 'MMM d')}: selectedHours=[${selectedHourNumbers.join(', ')}], currentHours=[${currentHourNumbers.join(', ')}], isEditing=${isEditing}`);
  
  // Check if this is a weekday (Monday-Friday)
  const isWeekday = date.getDay() >= 1 && date.getDay() <= 5;
  

  // Initialize editing state
  useEffect(() => {
    if (isEditing) {
      setSelectedHours(currentHours);
      setNewSlotJobTypes(currentJobTypes.length > 0 ? currentJobTypes : getDefaultJobTypes(preferences));
      setNotes(currentNotes);
      setIsMarkedUnavailable(currentIsUnavailable);
      setHasUserInteracted(false); // Reset interaction flag when starting to edit
    }
  // ESLINT NOTE: Intentionally omitting currentHours, currentJobTypes, currentNotes, currentIsUnavailable —
  // these are the server-side "current" values used only to seed the editor on open. Adding them
  // would re-initialize and overwrite user edits any time the server state refreshes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, dateStr, preferences]); // Add preferences dependency

  // Auto-open edit mode for available days and weekdays (only if editable)
  useEffect(() => {
    if ((isAvailable || isWeekday) && canEdit) {
      setIsEditing(true);
    }
  }, [isAvailable, isWeekday, dateStr, canEdit]); // Add dateStr and canEdit dependency

  // Auto-save when job types change
  useEffect(() => {
    if (isEditing && hasUserInteracted) {
      devLog('📋 Job types changed, calling queueSave:', newSlotJobTypes);
      queueSave();
    }
  // ESLINT NOTE: Intentionally omitting isEditing, hasUserInteracted, queueSave — this effect should only
  // fire when newSlotJobTypes actually changes. Adding the guards would trigger a save whenever
  // editing mode or interaction state changes, not just when the user picks a new job type.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSlotJobTypes]);

  // Auto-save when notes change
  useEffect(() => {
    if (isEditing && hasUserInteracted && notes !== currentNotes) {
      devLog(`📝 Notes changed from "${currentNotes}" to "${notes}", calling queueSave`);
      queueSave();
    }
  // ESLINT NOTE: Intentionally omitting isEditing, hasUserInteracted, currentNotes, queueSave — same pattern
  // as the other auto-save effects: should only fire when the user's note text changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  // Auto-save when selected hours change
  useEffect(() => {
    if (isEditing && hasUserInteracted) {
      const selectedHourNumbers = Object.keys(selectedHours).map(h => parseInt(h)).sort((a, b) => a - b);
      devLog(`⏰ selectedHours changed: [${selectedHourNumbers.join(', ')}], calling queueSave`);
      queueSave();
    }
  // ESLINT NOTE: Intentionally omitting isEditing, hasUserInteracted, queueSave — same pattern as other
  // auto-save effects: should only fire when the selected hours actually change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHours]);

  // Auto-save when unavailable status changes
  useEffect(() => {
    if (isEditing && hasUserInteracted && isMarkedUnavailable !== currentIsUnavailable) {
      devLog(`🚫 Unavailable status changed from ${currentIsUnavailable} to ${isMarkedUnavailable}, calling queueSave`);
      queueSave();
    }
  // Intentionally omitting isEditing, hasUserInteracted, currentIsUnavailable, queueSave — same
  // pattern as other auto-save effects: should only fire when the unavailable toggle changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMarkedUnavailable]);

  const handleUnavailableToggle = (checked: boolean) => {
    if (!canEdit) {
      setShowDeadlineWarning(true);
      setTimeout(() => setShowDeadlineWarning(false), 3000);
      return;
    }
    setIsMarkedUnavailable(checked);
    if (checked) {
      // Clear all selections when marking as unavailable
      setSelectedHours({});
      setNotes('');
    }
    setHasUserInteracted(true);
  };

  const selectPreset = (preset: typeof QUICK_PRESETS[0]) => {
    if (!canEdit || isMarkedUnavailable) {
      setShowDeadlineWarning(true);
      setTimeout(() => setShowDeadlineWarning(false), 3000);
      return;
    }
    devLog(`🎯 selectPreset called: ${preset.label} with hours [${preset.hours.join(', ')}]`);
    setHasUserInteracted(true);
    
    // Check if this preset is currently selected - if so, toggle it off
    const selectedHourNumbers = Object.keys(selectedHours).map(h => parseInt(h));
    const isPresetSelected = preset.hours.every(hour => selectedHourNumbers.includes(hour)) && 
                             preset.hours.length > 0 &&
                             selectedHourNumbers.filter(hour => preset.hours.includes(hour)).length === preset.hours.length;
    
    if (isPresetSelected) {
      // Remove these hours
      setSelectedHours(prev => {
        const newHours = { ...prev };
        preset.hours.forEach(hour => {
          delete newHours[hour];
        });
        return newHours;
      });
    } else {
      // Add these hours (merge with existing)
      setSelectedHours(prev => {
        const newHours = { ...prev };
        preset.hours.forEach(hour => {
          if (!newHours[hour]) {
            newHours[hour] = [...newSlotJobTypes];
          }
        });
        return newHours;
      });
    }
    queueSave();
  };

  // Helper function to check if a preset is currently selected
  const isPresetSelected = (preset: typeof QUICK_PRESETS[0]): boolean => {
    const selectedHourNumbers = Object.keys(selectedHours).map(h => parseInt(h));
    return preset.hours.every(hour => selectedHourNumbers.includes(hour)) && preset.hours.length > 0;
  };

  const handleMouseDown = (hour: number, event: React.MouseEvent<HTMLButtonElement>) => {
    if (!canEdit) {
      setShowDeadlineWarning(true);
      setTimeout(() => setShowDeadlineWarning(false), 3000);
      event.preventDefault();
      return;
    }
    setHasUserInteracted(true);

    // Capture the original state before any changes
    const isCurrentlySelected = selectedHours[hour] !== undefined;
    
    // Set drag state with original selection state
    setIsDragging(true);
    setDragStartHour(hour);
    setDragStartWasSelected(isCurrentlySelected);
    
    // Toggle the starting hour
    if (isCurrentlySelected) {
      setSelectedHours(prev => {
        const newHours = { ...prev };
        delete newHours[hour];
        devLog(`⏰ Removing hour ${hour}`);
        return newHours;
      });
    } else {
      setSelectedHours(prev => {
        const newHours = { ...prev };
        newHours[hour] = [...newSlotJobTypes];
        devLog(`⏰ Adding hour ${hour} with job types [${newSlotJobTypes.join(', ')}]`);
        return newHours;
      });
    }
    
    // Blur the button after state updates (small timeout)
    setTimeout(() => {
      event.currentTarget?.blur();
    }, 10);
  };

  const handleMouseEnter = (hour: number) => {
    if (isDragging && dragStartHour !== null) {
      const startHour = Math.min(dragStartHour, hour);
      const endHour = Math.max(dragStartHour, hour);
      const rangeHours: number[] = [];
      for (let i = startHour; i <= endHour; i++) {
        rangeHours.push(i);
      }
      
      if (dragStartWasSelected) {
        // Deselecting - remove all hours in range
        setSelectedHours(prev => {
          const newHours = { ...prev };
          rangeHours.forEach(h => {
            delete newHours[h];
          });
          devLog(`🖱️ Deselecting range: [${rangeHours.join(', ')}]`);
          return newHours;
        });
      } else {
        // Selecting - add all hours in range
        setSelectedHours(prev => {
          const newHours = { ...prev };
          rangeHours.forEach(h => {
            if (!newHours[h]) {
              newHours[h] = [...newSlotJobTypes];
            }
          });
          devLog(`🖱️ Selecting range: [${rangeHours.join(', ')}]`);
          return newHours;
        });
      }
    }
  };

  const handleMouseUp = () => {
    devLog('🖱️ handleMouseUp called, dragging was:', isDragging, 'dragStartHour was:', dragStartHour);
    setIsDragging(false);
    setDragStartHour(null);
    setDragStartWasSelected(false);
    if (isDragging) {
      devLog('🖱️ Drag completed - calling queueSave');
      queueSave(); // Save after drag selection
    }
  };

  // Add global mouse up listener to handle mouse release outside buttons
  useEffect(() => {
    const handleGlobalMouseUp = () => handleMouseUp();
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  // ESLINT NOTE: Intentionally omitting handleMouseUp — it's an inline function with a new reference every
  // render. Adding it would re-register the global mouseup listener on every render. The closure
  // captures isDragging by reference so the handler always sees the current drag state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedSave = async () => {
    // Don't save if editing is not allowed
    if (!canEdit) {
      devLog('⚠️ Cannot save - editing not allowed');
      return;
    }

    devLog('💾 debouncedSave started');
    const selectedHourNumbers = Object.keys(selectedHours).map(h => parseInt(h)).sort((a, b) => a - b);
    devLog(`⏰ Selected hours: [${selectedHourNumbers.join(', ')}]`);

    try {
      if (isMarkedUnavailable) {
        devLog('🚫 Marked as unavailable - saving with unavailable flag');
        const payload = {
          availabilityDate: dateStr,
          hourSlots: 'NNNNNNNNNNNN',
          notes: '',
          unavailable: true
        };
        const result = await availabilityApi.setAvailability(payload);
        devLog('✅ API response:', result);
      } else if (selectedHourNumbers.length === 0) {
        devLog('❌ No hours selected - removing availability');
        try {
          await availabilityApi.removeAvailability(dateStr);
          devLog('✅ Removed availability for date');
        } catch (error) {
          devLog('ℹ️ No existing record to remove or removal failed:', error);
        }
      } else {
        devLog('✅ Hours selected - encoding as hour_slots');

        // Encode the selected hours into the hour_slots format
        const hourSlotsString = HourSlotsEncoder.encode(selectedHours);
        devLog(`📦 Encoded hour slots: ${hourSlotsString}`);

        // Create the payload with encoded hour_slots
        const payload = {
          availabilityDate: dateStr,
          hourSlots: hourSlotsString,
          notes: notes || undefined,
          unavailable: false
        };
        const result = await availabilityApi.setAvailability(payload);
        devLog('✅ API response:', result);
      }

      devLog('✅ Save completed successfully - updating local state');

      // Update the parent's local availability state
      onUpdate(dateStr, {
        hourSlots: isMarkedUnavailable ? 'NNNNNNNNNNNN' : (selectedHourNumbers.length > 0 ? HourSlotsEncoder.encode(selectedHours) : 'NNNNNNNNNNNN'),
        notes: isMarkedUnavailable ? '' : notes,
        unavailable: isMarkedUnavailable
      });

      // Show success notification
      onNotification(`${format(date, 'EEE M/d')} saved`, 'success');
    } catch (error) {
      console.error('❌ Error saving availability:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);

        // Show deadline message if editing is blocked
        if (error.message.includes('Schedule cannot be edited')) {
          setShowDeadlineWarning(true);
          setTimeout(() => setShowDeadlineWarning(false), 5000);
          onNotification('Schedule editing deadline has passed', 'error');
        } else {
          onNotification('Failed to save availability', 'error');
        }
      } else {
        onNotification('Failed to save availability', 'error');
      }
    }
  };

  const queueSave = () => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    const timeout = setTimeout(() => {
      devLog('⏰ timeout triggered - calling debouncedSave');
      debouncedSave();
    }, 1000); // Save after 1 second of inactivity
    setSaveTimeout(timeout);
  };

  return (
    <Card
      sx={{
        minHeight: isEditing ? 450 : 160,
        border: dayIsToday 
          ? `2px solid ${theme.palette.primary.main}` 
          : isAvailable 
            ? `2px solid #ccc` 
            : '1px solid #e0e0e0',
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        '& .MuiPaper-root': {
          padding: '24px !important'
        },
        padding: '24px !important' // Force override default Card padding
      }}
    >
      <CardContent sx={{ 
        p: 0, // Remove CardContent padding since Card has padding
        '&:last-child': { pb: 0 }, 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        {/* Compact Day Header - Hidden on mobile since date is in page header */}
        {!isMobile && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="subtitle2" component="div" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                {format(date, 'EEE')}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                {format(date, 'd')}
              </Typography>
              {dayIsToday && (
                <Chip 
                  label="Today" 
                  size="small" 
                  color="primary"
                  sx={{ fontSize: '0.6rem', height: 16, '& .MuiChip-label': { px: 0.5 } }}
                />
              )}
            </Box>
            {/* Not Available toggle on the right */}
            {isEditing && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isMarkedUnavailable}
                    onChange={(e) => handleUnavailableToggle(e.target.checked)}
                    color="error"
                    size="medium"
                    sx={{
                      width: 48,
                      '& .MuiSwitch-switchBase': {
                        '&.Mui-checked': {
                          transform: 'translateX(20px)',
                        },
                      },
                      '& .MuiSwitch-track': {
                        borderRadius: 26 / 2,
                        // opacity: 1,
                        // backgroundColor: 'rgba(0,0,0,.25)',
                        // boxSizing: 'border-box',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ ml: 1 }}>
                    Not Available
                  </Typography>
                }
                sx={{ mr: 0 }}
              />
            )}
            
            <Box display="flex" alignItems="center" gap={0.5}>
              {/* Icons moved to individual hour slots */}
            </Box>
          </Box>
        )}

        {/* Show deadline warning if past deadline */}
        {showDeadlineWarning && !canEdit && (
          <Alert severity="warning" sx={{ mb: 1, fontSize: '0.75rem' }}>
            Schedule editing closed. {editDeadline}
          </Alert>
        )}

        {!isEditing && !isAvailable && !isWeekday ? (
          /* Compact Display Mode - Only for unavailable weekend days */
          <Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1 }}>
                No hours selected
              </Typography>
              {currentNotes && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontStyle: 'italic', display: 'block', mb: 1 }}>
                  {currentNotes}
                </Typography>
              )}
            </Box>
            
            <Button
              size="medium"
              variant="outlined"
              onClick={() => {
                if (!canEdit) {
                  setShowDeadlineWarning(true);
                  setTimeout(() => setShowDeadlineWarning(false), 3000);
                } else {
                  setIsEditing(true);
                }
              }}
              disabled={!canEdit}
              sx={{ fontSize: '0.8rem', py: 0.75, px: 2, mt: 1 }}
              fullWidth
            >
              {canEdit ? 'Set Available' : 'Editing Closed'}
            </Button>
          </Box>
        ) : (
          /* Compact Edit Mode */
          <Box display="flex" flexDirection="column" flex={1}>
            {!isMobile && (
              <Divider sx={{ mb: 1 }} />
            )}
            
            {/* Not Available toggle for mobile only */}
            {isMobile && (
              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isMarkedUnavailable}
                      onChange={(e) => handleUnavailableToggle(e.target.checked)}
                      color="error"
                      size="medium"
                      sx={{
                        width: 48,
                        '& .MuiSwitch-switchBase': {
                          '&.Mui-checked': {
                            transform: 'translateX(20px)',
                            color: '#fff',
                          },
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 26 / 2,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ ml: 1 }}>
                      Not Available
                    </Typography>
                  }
                />
              </Box>
            )}
            
            {/* Compact Job Types */}
            <ToggleButtonGroup
              value={newSlotJobTypes}
              onChange={(_, newTypes) => {
                if (!canEdit || isMarkedUnavailable) {
                  setShowDeadlineWarning(true);
                  setTimeout(() => setShowDeadlineWarning(false), 3000);
                  return;
                }
                if (newTypes.length > 0) {
                  setHasUserInteracted(true);
                  setNewSlotJobTypes(newTypes);
                }
              }}
              size="small"
              sx={{ mb: 0.5, display: 'flex', opacity: isMarkedUnavailable ? 0.5 : 1 }}
              disabled={!canEdit || isMarkedUnavailable}
            >
              <ToggleButton value="virtual" sx={{ flex: 1, fontSize: isMobile ? (isLargeMobile ? '0.7rem' : '0.6rem') : '0.6rem', py: isMobile ? (isLargeMobile ? 0.3 : 0.2) : 0.2, minHeight: isMobile ? (isLargeMobile ? 24 : 20) : 20 }}>
                Virtual
              </ToggleButton>
              <ToggleButton value="in-person" sx={{ flex: 1, fontSize: isMobile ? (isLargeMobile ? '0.7rem' : '0.6rem') : '0.6rem', py: isMobile ? (isLargeMobile ? 0.3 : 0.2) : 0.2, minHeight: isMobile ? (isLargeMobile ? 24 : 20) : 20 }}>
                In-Person
              </ToggleButton>
            </ToggleButtonGroup>
            
            {/* Ultra Compact Quick Presets */}
            <Stack direction="row" spacing={0.1} sx={{ mb: 0.25 }}>
              {QUICK_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  size="small"
                  variant={isPresetSelected(preset) ? "contained" : "outlined"}
                  color={isPresetSelected(preset) ? "primary" : "inherit"}
                  onClick={(event) => {
                    if (!isMarkedUnavailable) {
                      selectPreset(preset);
                    }
                    setTimeout(() => {
                      event.currentTarget?.blur();
                    }, 10);
                  }}
                  sx={{ 
                    fontSize: isMobile ? (isLargeMobile ? '0.8rem' : '0.7rem') : '0.4rem', 
                    minWidth: 'auto !important',
                    width: 'auto',
                    minHeight: 'auto',
                    flex: 1,
                    paddingLeft: '6px !important',
                    paddingRight: '6px !important',
                    paddingTop: isMobile ? (isLargeMobile ? '14px !important' : '12px !important') : '12px !important',
                    paddingBottom: isMobile ? (isLargeMobile ? '14px !important' : '12px !important') : '12px !important',
                    opacity: isMarkedUnavailable ? 0.5 : 1,
                    '&.MuiButton-root': {
                      padding: isMobile ? (isLargeMobile ? '14px 6px !important' : '12px 6px !important') : '12px 6px !important'
                    },
                    '&.MuiButtonBase-root': {
                      padding: isMobile ? (isLargeMobile ? '14px 6px !important' : '12px 6px !important') : '12px 6px !important'
                    },
                    '&:focus': {
                      backgroundColor: isPresetSelected(preset) ? undefined : 'transparent !important',
                      borderColor: isPresetSelected(preset) ? undefined : '#e0e0e0 !important'
                    },
                    '&:active': {
                      backgroundColor: isPresetSelected(preset) ? undefined : 'transparent !important'
                    },
                    '&.MuiButton-outlined': {
                      borderColor: isPresetSelected(preset) ? undefined : '#e0e0e0 !important'
                    }
                  }}
                  disabled={isMarkedUnavailable}
                >
                  {preset.label === 'Morning' ? 'AM' : preset.label === 'Afternoon' ? 'PM' : 'All'}
                </Button>
              ))}
            </Stack>

            {/* Single Column - All 12 Hours Vertically - Take remaining space */}
            <Box display="flex" flexDirection="column" gap={0.1} sx={{ mb: 0.25, flex: 1, overflow: 'hidden', opacity: isMarkedUnavailable ? 0.5 : 1 }}>
              {HOUR_SLOTS.map((slot) => {
                const colors = getSlotColors(selectedHours[slot.value]);
                return (
                  <Button
                    key={slot.value}
                    size="small"
                    variant="outlined"
                    onMouseDown={(event) => handleMouseDown(slot.value, event)}
                    onMouseEnter={() => handleMouseEnter(slot.value)}
                    sx={{ 
                      fontSize: isMobile ? (isLargeMobile ? '0.8rem' : '0.7rem') : '0.55rem', 
                      minHeight: isMobile ? (isLargeMobile ? 32 : 24) : 14,
                      height: isMobile ? (isLargeMobile ? 32 : 24) : 14,
                      py: 0,
                      px: 0.25,
                      minWidth: 'auto',
                      width: '100%',
                      userSelect: 'none',
                      borderWidth: '1px',
                      overflow: 'visible',
                      position: 'relative',
                      borderColor: `${colors.borderColor} !important`,
                      backgroundColor: colors.backgroundColor,
                      color: selectedHours[slot.value] ? 'white !important' : '#333 !important',
                      '&.MuiButton-outlined': {
                        backgroundColor: `${colors.backgroundColor} !important`,
                        borderColor: `${colors.borderColor} !important`,
                        color: selectedHours[slot.value] ? 'white !important' : '#333 !important',
                      },
                      '&:hover': {
                        backgroundColor: isDragging ? 'rgba(0,0,0,0.1)' : 
                                       selectedHours[slot.value] ? `${colors.hoverBackgroundColor} !important` : 
                                       `${colors.hoverBackgroundColor} !important`,
                        borderColor: selectedHours[slot.value] ? `${colors.hoverBorderColor} !important` : `${colors.hoverBorderColor} !important`
                      },
                      '&:focus': {
                        backgroundColor: `${colors.backgroundColor} !important`,
                        borderColor: `${colors.borderColor} !important`,
                        color: selectedHours[slot.value] ? 'white !important' : '#333 !important',
                      },
                      '&:active': {
                        backgroundColor: `${colors.backgroundColor} !important`,
                        borderColor: `${colors.borderColor} !important`,
                        color: selectedHours[slot.value] ? 'white !important' : '#333 !important',
                      },
                      // Override Material-UI's specific focus classes
                      '&.MuiButtonBase-root.MuiButton-outlinedPrimary:hover': {
                        backgroundColor: isDragging ? 'rgba(0,0,0,0.1)' : 
                                       selectedHours[slot.value] ? `${colors.hoverBackgroundColor} !important` : 
                                       `${colors.hoverBackgroundColor} !important`,
                        borderColor: selectedHours[slot.value] ? `${colors.hoverBorderColor} !important` : `${colors.hoverBorderColor} !important`,
                        color: selectedHours[slot.value] ? 'white !important' : '#333 !important',
                      },
                      '&.MuiButtonBase-root.MuiButton-outlinedPrimary:focus': {
                        backgroundColor: `${colors.backgroundColor} !important`,
                        borderColor: `${colors.borderColor} !important`,
                        color: selectedHours[slot.value] ? 'white !important' : '#333 !important',
                      }
                    }}
                    title={`${slot.label} - Selected: ${selectedHours[slot.value] ? 'YES' : 'NO'} - Job Types: [${selectedHours[slot.value]?.join(', ') || 'none'}]`}
                  >
                    <>
                      {selectedHours[slot.value] && selectedHours[slot.value].includes('virtual') && (
                        <ComputerIcon sx={{ 
                          position: 'absolute', 
                          left: 6, 
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: isMobile ? (isLargeMobile ? '16px' : '14px') : '12px', 
                          color: 'white',
                          zIndex: 10
                        }} />
                      )}
                      {selectedHours[slot.value] && selectedHours[slot.value].includes('in-person') && (
                        <PersonIcon sx={{ 
                          position: 'absolute', 
                          right: 6, 
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: isMobile ? (isLargeMobile ? '16px' : '14px') : '12px', 
                          color: 'white',
                          zIndex: 10
                        }} />
                      )}
                      <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
                        <Typography variant="inherit" component="span">
                          {slot.label}
                        </Typography>
                      </Box>
                    </>
                  </Button>
                );
              })}
            </Box>

            {/* Compact Notes */}
            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => {
                if (!canEdit || isMarkedUnavailable) {
                  setShowDeadlineWarning(true);
                  setTimeout(() => setShowDeadlineWarning(false), 3000);
                  return;
                }
                setHasUserInteracted(true);
                setNotes(e.target.value);
              }}
              size="small"
              fullWidth
              multiline
              rows={2}
              disabled={!canEdit || isMarkedUnavailable}
              sx={{ 
                mb: 0.25,
                mt: 0.5, // Add top margin to prevent overlap
                opacity: isMarkedUnavailable ? 0.5 : 1,
                '& .MuiInputLabel-root': { fontSize: isMobile ? (isLargeMobile ? '0.7rem' : '0.6rem') : '0.6rem' }, 
                '& .MuiInputBase-input': { fontSize: isMobile ? (isLargeMobile ? '0.7rem' : '0.6rem') : '0.6rem', py: isMobile ? (isLargeMobile ? 0.4 : 0.25) : 0.25 },
                '& .MuiInputBase-root': { minHeight: 'auto' } // Remove fixed height for multiline
              }}
            />

          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const SchedulePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeMobile = useMediaQuery('(max-width: 600px) and (min-height: 700px)');
  const [availability, setAvailability] = useState<ReporterAvailability | null>(null);
  const [preferences, setPreferences] = useState<ReporterPreferences | null>(null);
  const [currentWeek, setCurrentWeek] = useState<Date>(new Date());
  const [currentDayIndex, setCurrentDayIndex] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [weekEditability, setWeekEditability] = useState<{ canEdit: boolean; deadline: string } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Notification handler for DaySchedule components
  const showNotification = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Load availability and preferences data
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load both availability and preferences in parallel
      const [availabilityData, preferencesData] = await Promise.all([
        availabilityApi.getAvailability(),
        preferencesApi.getPreferences()
      ]);
      
      setAvailability(availabilityData);
      setPreferences(preferencesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if the current week can be edited
  const checkWeekEditability = async () => {
    try {
      const weekDays = getWeekDays(currentWeek);
      if (weekDays.length > 0) {
        // Check the first day of the week
        const firstDay = format(weekDays[0], 'yyyy-MM-dd');
        const editability = await availabilityApi.checkEditable(firstDay);
        setWeekEditability({
          canEdit: editability.canEdit,
          deadline: editability.reason || editability.deadlineFormatted
        });
      }
    } catch (err) {
      console.error('Error checking week editability:', err);
    }
  };

  // Update local availability state when a day is modified
  const updateLocalAvailability = (dateStr: string, updatedData: AvailabilityUpdate | null) => {
    if (!availability) return;
    
    setAvailability(prev => {
      if (!prev) return prev;
      
      // Create a deep copy of the availability data
      const newAvailability = { ...prev };
      newAvailability.availability = [...prev.availability];
      
      // Find existing record for this date
      const existingIndex = newAvailability.availability.findIndex(record => {
        const recordDate = record.availabilityDate.includes('T')
          ? record.availabilityDate.split('T')[0]
          : record.availabilityDate;
        return recordDate === dateStr;
      });
      
      if (updatedData === null) {
        // Remove the record if it exists
        if (existingIndex !== -1) {
          newAvailability.availability.splice(existingIndex, 1);
        }
      } else if (updatedData.unavailable) {
        // Explicitly marked as unavailable - create/update record with unavailable flag
        const reporterId = existingIndex !== -1 
          ? prev.availability[existingIndex].reporterId 
          : prev.reporterId;
        
        const newRecord = {
          reporterId,
          availabilityDate: dateStr,
          hourSlots: 'NNNNNNNNNNNN',
          notes: '',
          unavailable: true
        };
        
        if (existingIndex !== -1) {
          // Update existing record
          newAvailability.availability[existingIndex] = newRecord;
        } else {
          // Add new record
          newAvailability.availability.push(newRecord);
        }
      } else if (updatedData.hourSlots && updatedData.hourSlots !== 'NNNNNNNNNNNN') {
        // Create or update the record
        const reporterId = existingIndex !== -1 
          ? prev.availability[existingIndex].reporterId 
          : prev.reporterId;
        
        const newRecord = {
          reporterId,
          availabilityDate: dateStr,
          hourSlots: updatedData.hourSlots,
          notes: updatedData.notes || undefined,
          unavailable: false
        };
        
        if (existingIndex !== -1) {
          // Update existing record
          newAvailability.availability[existingIndex] = newRecord;
        } else {
          // Add new record
          newAvailability.availability.push(newRecord);
        }
      } else {
        // No availability - remove the record
        if (existingIndex !== -1) {
          newAvailability.availability.splice(existingIndex, 1);
        }
      }
      
      return newAvailability;
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  // Check editability when week changes
  useEffect(() => {
    checkWeekEditability();
  // ESLINT NOTE: Intentionally omitting checkWeekEditability — it's an inline function with a new reference
  // every render. Adding it would cause this effect to re-run on every render, not just when the
  // week changes. The editability check only needs to run when the user navigates to a new week.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeek]);

  // Get days for the current week (Monday to Friday only)
  const getWeekDays = (date: Date) => {
    const start = startOfWeek(date, { weekStartsOn: 1 }); // Start on Monday
    const end = endOfWeek(date, { weekStartsOn: 1 });
    const allDays = eachDayOfInterval({ start, end });
    // Filter to only include weekdays (Monday = 1, Friday = 5)
    return allDays.filter(day => {
      const dayOfWeek = day.getDay();
      return dayOfWeek >= 1 && dayOfWeek <= 5;
    });
  };

  const goToPreviousWeek = () => {
    setCurrentWeek(prev => subWeeks(prev, 1));
  };

  const goToNextWeek = () => {
    setCurrentWeek(prev => addWeeks(prev, 1));
  };

  const goToCurrentWeek = () => {
    setCurrentWeek(new Date());
  };

  const copyLastWeek = async () => {
    try {
      const currentWeekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
      const currentWeekStartStr = format(currentWeekStart, 'yyyy-MM-dd');
      
      await availabilityApi.copyLastWeek(currentWeekStartStr);
      
      // Reload availability data
      loadData();
    } catch (error) {
      console.error('Error copying last week:', error);
    }
  };

  // Mobile day navigation
  const goToPreviousDay = () => {
    if (currentDayIndex > 0) {
      setCurrentDayIndex(currentDayIndex - 1);
    } else {
      // Go to previous week, last day
      setCurrentWeek(prev => subWeeks(prev, 1));
      setCurrentDayIndex(4); // Friday (last weekday)
    }
  };

  const goToNextDay = () => {
    const weekDays = getWeekDays(currentWeek);
    if (currentDayIndex < weekDays.length - 1) {
      setCurrentDayIndex(currentDayIndex + 1);
    } else {
      // Go to next week, first day
      setCurrentWeek(prev => addWeeks(prev, 1));
      setCurrentDayIndex(0); // Monday
    }
  };

  const handleDateSelect = (selectedDate: Date) => {
    // Find the week containing the selected date
    const selectedWeek = startOfWeek(selectedDate, { weekStartsOn: 1 });
    setCurrentWeek(selectedWeek);
    
    // Find the day index within that week (0=Monday, 4=Friday)
    const dayOfWeek = selectedDate.getDay();
    const weekdayIndex = dayOfWeek === 0 ? -1 : dayOfWeek - 1; // Convert to Monday=0 indexing
    
    // Only set valid weekday indices (0-4 for Mon-Fri)
    if (weekdayIndex >= 0 && weekdayIndex <= 4) {
      setCurrentDayIndex(weekdayIndex);
    }
    
    // Close the calendar picker
    setShowCalendarPicker(false);
  };

  // Touch handlers for swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isMobile || touchStartX === null) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;
    const minSwipeDistance = 50; // Minimum distance for a swipe

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        // Swipe left - go to next day
        goToNextDay();
      } else {
        // Swipe right - go to previous day
        goToPreviousDay();
      }
    }
    
    setTouchStartX(null);
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header />
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          flex: '1 0 auto',
          gap: 2,
          p: 4
        }}>
          <CircularProgress size={48} />
          <Typography variant="h6">Loading your schedule...</Typography>
        </Box>
        <Footer />
      </Box>
    );
  }

  const weekDays = getWeekDays(currentWeek);
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  
  // Ensure currentDayIndex is within bounds
  const safeCurrentDayIndex = Math.max(0, Math.min(currentDayIndex, weekDays.length - 1));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header />
      
      <Box component="main" sx={{ flexGrow: 1, px: 0.5, py: 1, overflow: 'auto' }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 1 }} 
            onClose={() => setError(null)}
            action={
              // Check if the error indicates authentication failure
              error.includes('session has expired') || error.includes('log in') ? (
                <Button 
                  color="inherit" 
                  size="small" 
                  href="/login"
                  sx={{ ml: 2 }}
                >
                  Log In
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        )}

        {/* Show week-level deadline warning */}
        {weekEditability && !weekEditability.canEdit && (
          <Alert severity="warning" sx={{ mb: 2, mx: 1 }}>
            <Typography variant="body2">
              Schedule editing for this week is closed. {weekEditability.deadline}
            </Typography>
          </Alert>
        )}

        {/* Navigation Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1, px: 1 }}>
          {isMobile ? (
            <IconButton 
              onClick={goToPreviousDay} 
              size="large"
              sx={{ p: isLargeMobile ? 1.5 : 1.2 }}
            >
              <ChevronLeftIcon fontSize={isLargeMobile ? "large" : "medium"} />
            </IconButton>
          ) : (
            <Button
              onClick={goToPreviousWeek}
              startIcon={<ChevronLeftIcon />}
              size="small"
              variant="outlined"
              sx={{ 
                fontSize: '0.8rem', 
                py: 0.25, 
                px: 1.5,
                minHeight: '32px',
                height: '32px'
              }}
            >
              Previous
            </Button>
          )}
          
          <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2}>
            {isMobile && (
              <IconButton
                size="small"
                onClick={() => setShowCalendarPicker(true)}
                sx={{ p: 0.5 }}
              >
                <CalendarTodayIcon fontSize="small" />
              </IconButton>
            )}
            
            <Typography variant={isMobile ? (isLargeMobile ? "h6" : "subtitle1") : "h6"} sx={{ fontSize: isMobile && isLargeMobile ? '1.1rem' : undefined }}>
              {isMobile 
                ? format(weekDays[safeCurrentDayIndex], 'EEE M/d/yy')
                : `${format(weekStart, 'MM/dd')}-${format(weekEnd, 'MM/dd')}`}
            </Typography>
            
            {!isMobile && (
              <>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={goToCurrentWeek}
                  disabled={format(currentWeek, 'yyyy-ww') === format(new Date(), 'yyyy-ww')}
                  sx={{ 
                    fontSize: '0.7rem', 
                    py: 0.25, 
                    px: 1,
                    minHeight: '32px',
                    height: '32px'
                  }}
                >
                  Today
                </Button>
                <Tooltip title={weekEditability && !weekEditability.canEdit ? weekEditability.deadline : ''}>
                  <span>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={copyLastWeek}
                      disabled={weekEditability ? !weekEditability.canEdit : false}
                      sx={{ 
                        fontSize: '0.7rem', 
                        py: 0.25, 
                        px: 1,
                        minHeight: '32px',
                        height: '32px'
                      }}
                    >
                      Copy Last Week
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
            
            <IconButton
              size="small"
              onClick={() => navigate('/account-settings?tab=availability-preferences')}
              sx={{ p: 0.5 }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
            
            {/* Inline Compact Legend - Desktop only */}
            {!isMobile && (
              <Box display="flex" alignItems="center" gap={1} sx={{ fontSize: '0.7rem' }}>
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Box sx={{ width: 8, height: 8, backgroundColor: '#42a5f5', borderRadius: '50%' }} />
                  <ComputerIcon sx={{ fontSize: 10, color: '#42a5f5' }} />
                  <Typography variant="caption" fontSize="0.65rem">Virtual</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Box sx={{ width: 8, height: 8, backgroundColor: '#e57373', borderRadius: '50%' }} />
                  <PersonIcon sx={{ fontSize: 10, color: '#e57373' }} />
                  <Typography variant="caption" fontSize="0.65rem">In-person</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Box sx={{ width: 8, height: 8, backgroundColor: '#7e57c2', borderRadius: '50%' }} />
                  <Typography variant="caption" fontSize="0.65rem">Both</Typography>
                </Box>
              </Box>
            )}
          </Box>
          
          {isMobile ? (
            <IconButton 
              onClick={goToNextDay} 
              size="large"
              sx={{ p: isLargeMobile ? 1.5 : 1.2 }}
            >
              <ChevronRightIcon fontSize={isLargeMobile ? "large" : "medium"} />
            </IconButton>
          ) : (
            <Button
              onClick={goToNextWeek}
              endIcon={<ChevronRightIcon />}
              size="small"
              variant="outlined"
              sx={{ 
                fontSize: '0.8rem', 
                py: 0.25, 
                px: 1.5,
                minHeight: '32px',
                height: '32px'
              }}
            >
              Next
            </Button>
          )}
        </Box>

        {/* Schedule View */}
        {isMobile ? (
          /* Mobile: Single Day View */
          <Box 
            sx={{ px: 1 }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <DaySchedule
              date={weekDays[safeCurrentDayIndex]}
              availability={availability}
              preferences={preferences}
              onUpdate={updateLocalAvailability}
              onNotification={showNotification}
              isMobile={true}
              canEdit={weekEditability?.canEdit ?? true}
              editDeadline={weekEditability?.deadline}
            />
          </Box>
        ) : (
          /* Desktop: Week View Grid */
          <Box sx={{ overflowX: 'auto', pb: 0.25, px: 2 }}>
            <Grid container spacing={0.5} sx={{ minWidth: '1260px' }}>
              {weekDays.map((date) => (
                <Grid item key={format(date, 'yyyy-MM-dd')} sx={{ minWidth: '180px', flex: '1 1 180px' }}>
                  <DaySchedule
                    date={date}
                    availability={availability}
                    preferences={preferences}
                    onUpdate={updateLocalAvailability}
                    onNotification={showNotification}
                    isMobile={false}
                    canEdit={weekEditability?.canEdit ?? true}
                    editDeadline={weekEditability?.deadline}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>

      <Footer />
      
      {/* Mobile Calendar Picker Dialog */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300,
            display: showCalendarPicker ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onClick={() => setShowCalendarPicker(false)}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <AvailabilityCalendar onDateSelect={handleDateSelect} openEditorOnSelect={false} />
          </Box>
        </Box>
      )}

      {/* Snackbar for save notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchedulePage;